import React from 'react';
import { Checkbox, Col, Form, Input, Radio, Row, Space, Typography } from 'antd';
import type { FormInstance } from 'antd';
import CheckAllButtons from '../CheckAllButtons';
import WeldMetaBlock from '../WeldMetaBlock';
import { SECTION_HEADER_WIDTH } from '../types';
import type { WeldMeta } from '../types';

const { Text } = Typography;

export interface MtPanelProps {
  form: FormInstance;
  weldKey: string;
  isView: boolean;
  meta?: WeldMeta;
}

/** MT (Magnetic Particle Test) detay paneli — JSX PipeInspectionForm'dan birebir taşındı. */
export default function MtPanel({ form, weldKey, isView, meta }: MtPanelProps) {
  return (
    <div>
      {/* Weld meta info from RFI (read-only) */}
      <WeldMetaBlock meta={meta} showFluid={false} />
      <div style={{ padding: 4, backgroundColor: '#f6ffed', borderRadius: 4 }}>
        <Row gutter={[6, 4]}>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Prep.</Text>
              <Form.Item name={['results', weldKey, 'mt', 'preparation', 'clean']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Clean</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'mt', 'preparation', 'coating_removed']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Coating removed</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Method</Text>
              <Form.Item name={['results', weldKey, 'mt', 'method']} style={{ margin: 0 }}>
                <Radio.Group size="small" disabled={isView} onChange={() => {
                  const m = form.getFieldValue(['results', weldKey, 'mt', 'method']);
                  if (m !== 'yoke') {
                    form.setFieldsValue({ results: { [weldKey]: { mt: { lift_test: 'NA' } } } });
                  }
                }}>
                  <Radio value="yoke">Yoke</Radio>
                  <Radio value="prods">Prods</Radio>
                  <Radio value="coil">Coil</Radio>
                  <Radio value="central_conductor">Central Conductor</Radio>
                  <Radio value="other">Other</Radio>
                </Radio.Group>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Current</Text>
              <Form.Item name={['results', weldKey, 'mt', 'current', 'type']} style={{ margin: 0 }}>
                <Radio.Group size="small" disabled={isView}>
                  <Radio value="AC">AC</Radio>
                  <Radio value="DC">DC</Radio>
                </Radio.Group>
              </Form.Item>
              <Text>Amperes</Text>
              <Form.Item name={['results', weldKey, 'mt', 'current', 'amperes']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 100 }} placeholder="A" disabled={isView} />
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Form.Item shouldUpdate noStyle>
              {() => {
                const method = form.getFieldValue(['results', weldKey, 'mt', 'method']);
                const disabled = isView || method !== 'yoke';
                return (
                  <Space size={6} align="center" wrap>
                    <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Lift test</Text>
                    <Form.Item name={['results', weldKey, 'mt', 'lift_test']} initialValue={method === 'yoke' ? undefined : 'NA'} style={{ margin: 0 }}>
                      <Radio.Group size="small" disabled={disabled}>
                        <Radio value="AC_4_5kg">AC 4.5kg</Radio>
                        <Radio value="DC_18kg">DC18 kg</Radio>
                        <Radio value="NA">N/A</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Space>
                );
              }}
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Field Str.</Text>
              <Text>Flux Density</Text>
              <Form.Item name={['results', weldKey, 'mt', 'field_strength', 'flux_density']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 120 }} disabled={isView} />
              </Form.Item>
              <Form.Item name={['results', weldKey, 'mt', 'field_strength', 'units']} style={{ margin: 0 }}>
                <Radio.Group size="small" disabled={isView}>
                  <Radio value="gauss">Gaus.</Radio>
                  <Radio value="tesla">Tesla</Radio>
                </Radio.Group>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Particle</Text>
              <Form.Item name={['results', weldKey, 'mt', 'particle', 'type']} style={{ margin: 0 }}>
                <Radio.Group size="small" disabled={isView} onChange={() => {
                  const type = form.getFieldValue(['results', weldKey, 'mt', 'particle', 'type']);
                  let unit: 'lux' | 'uv' | undefined = undefined;
                  if (type === 'wet_fluorescent') unit = 'uv';
                  else if (type && type !== 'na') unit = 'lux';
                  form.setFieldsValue({ results: { [weldKey]: { mt: { lighting_unit: unit } } } });
                  if (type === 'na') {
                    form.setFieldsValue({ results: { [weldKey]: { mt: { lighting: undefined } } } });
                  }
                }}>
                  <Radio value="dry_visible">Dry-Visible</Radio>
                  <Radio value="wet_visible">Wet-Visible</Radio>
                  <Radio value="wet_fluorescent">Wet-Fluorescent</Radio>
                  <Radio value="na">N/A</Radio>
                </Radio.Group>
              </Form.Item>
              <Text>Brand/batch</Text>
              <Form.Item name={['results', weldKey, 'mt', 'particle', 'brand_batch']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 100 }} disabled={isView} />
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Lighting</Text>
              <Form.Item shouldUpdate noStyle>
                {() => {
                  const t = form.getFieldValue(['results', weldKey, 'mt', 'particle', 'type']);
                  const isNA = t === 'na' || !t;
                  return (
                    <Space size={6} align="center" wrap>
                      <Form.Item name={['results', weldKey, 'mt', 'lighting']} style={{ margin: 0 }}>
                        <Input size="small" style={{ width: 160 }} placeholder="Lux or UV intensity" disabled={isView || isNA} />
                      </Form.Item>
                      <Form.Item name={['results', weldKey, 'mt', 'lighting_unit']} style={{ margin: 0 }}>
                        <Radio.Group size="small" disabled>
                          <Radio value="lux">Lux</Radio>
                          <Radio value="uv">UV</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Space>
                  );
                }}
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Duration</Text>
              <Form.Item name={['results', weldKey, 'mt', 'development_time_min']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 100 }} type="number" step="1" placeholder="min" disabled={isView} />
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Indications</Text>
              <Form.Item name={['results', weldKey, 'mt', 'indications', 'no_cracks']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No cracks</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'mt', 'indications', 'proper_fusion']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Proper fusion</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'mt', 'indications', 'no_porosity']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No porosity</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'mt', 'indications', 'no_laps']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>No laps</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Finish</Text>
              <Form.Item name={['results', weldKey, 'mt', 'finish', 'post_cleaning']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Post‑cleaning</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="start" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Remarks</Text>
              <Form.Item name={['results', weldKey, 'mt', 'remarks']} style={{ margin: 0, flex: 1 }}>
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} style={{ minWidth: 480 }} disabled={isView} />
              </Form.Item>
              {!isView && (
                <CheckAllButtons
                  typeLabel="MT"
                  onSetAll={(v) => {
                    const k = weldKey;
                    const next = {
                      preparation: { clean: v, coating_removed: v },
                      particle: { ...(form.getFieldValue(['results', k, 'mt', 'particle']) || {}), particle_applied: v },
                      indications: { no_cracks: v, proper_fusion: v, no_porosity: v, no_laps: v },
                      finish: { post_cleaning: v },
                    };
                    form.setFieldsValue({ results: { [k]: { mt: next } } });
                  }}
                />
              )}
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );
}
